<template>
    <div
        class="d-flex flex-nowrap pe-2"
        v-if="value != null"
    >
        <span
            class="small me-3 flex-fill text-end fst-italic"
            :title="getTypeTitle()"
            v-html="getTypeText()"
        ></span>
        <div
            class="text-end fw-semibold"
            v-if="getValueType() == 'date'"
        >
            <DateComp
                :format="getDateFormat()"
                :value="internalValue as Date"
            />
        </div>
        <div
            class="text-end fw-semibold"
            v-else-if="getValueType() == 'timespan'"
        >
            <DisplayTimespan :value="internalValue as TimeSpan" />
        </div>
        <div
            class="text-end fw-semibold"
            v-else-if="getValueType() == 'number'"
        >
            {{ LocaleFormatNumeric(internalValue as number, undefined, computedOptions) }}
        </div>
    </div>
</template>
<script setup lang="ts">
import { DisplayTimespan, DateComp } from '@/components';
import { LocaleFormatNumeric } from '@/utilities/utils';
import type { ICellRendererParams } from 'ag-grid-community';
import { computed, ref } from 'vue';
import type { ICellRendererComponentParams } from '../Classes/params';
import type { IGridAggregate } from '../Classes/IGridAggregate';
import { $tm } from '@/locize';
import { TimeSpan } from '@/objects/TimeSpan';

const props = defineProps<{
    params: ICellRendererParams & ICellRendererComponentParams<any, any>;
}>();

const value: IGridAggregate | null = props.params.value ?? null;

const internalValue = ref<number | Date | TimeSpan>();

const computedOptions = computed<Intl.NumberFormatOptions | undefined>(() => {
    if (value?.decimals != null && value?.decimals >= 0) {
        return {
            minimumFractionDigits: value.decimals,
            maximumFractionDigits: value.decimals,
        };
    }

    return undefined;
});

if (value !== null) {
    internalValue.value = value.aggregate!(props.params.colDef?.colId!) ?? 0;
}

const getDateFormat = () => {
    if ('format' in props.params.componentProps) {
        return props.params.componentProps.format;
    }
    return 'TIME';
};

const getValueType = (): 'number' | 'date' | 'timespan' | 'string' => {
    if (internalValue.value instanceof Date) {
        return 'date';
    } else if (internalValue.value instanceof TimeSpan) {
        return 'timespan';
    } else if (typeof internalValue.value === 'number' && !isNaN(internalValue.value)) {
        return 'number';
    } else {
        return 'string';
    }
};

const getTypeText = () => {
    switch (value?.type) {
        case 'sum':
            return '&sum;';
        case 'avg':
            return 'x̄';
        case 'max':
            return 'max';
    }
};

const getTypeTitle = () => {
    switch (value?.type) {
        case 'sum':
            return $tm('math.sum');
        case 'avg':
            return $tm('math.avg');
        case 'max':
            return $tm('math.max');
    }
};
</script>
